/**
 * Data access for the partner network.
 *
 * Its own file rather than more of `queries.ts`, which is already long enough
 * that finding anything in it is a scroll. Same rules apply: every Supabase
 * call this feature makes is in here, so the column lists live in one place and
 * a schema change has one place to be fixed.
 *
 * Two things are deliberately *not* here:
 *
 *   * **Nothing sends a text.** The send layer is `sendChannel.ts`, and on the
 *     `phone` channel sending is a navigation, not a request.
 *   * **Nothing decides who gets a message.** That is `draft_partner_batch` in
 *     the database, called through `draftBatch` below. The rules about who
 *     hears from us are invariants, and invariants do not live in a browser.
 */

import type {
  PartnerCategory,
  PartnerCounty,
  StormTrigger,
} from "@roof-hawk/core";

import { supabase, unwrap } from "./supabase";

/**
 * One row, or null, cast to our own shape.
 *
 * The double cast is for the reason `unwrap` gives: without generated database
 * types, postgrest-js infers an embedded relation as an array even for a to-one
 * join, so `owner:profiles(full_name)` types as `{full_name}[]` and will not
 * narrow to the domain shape directly. The column lists in this file are the
 * source of truth, so a mismatch is a one-file fix.
 */
function row<T>(result: {
  data: unknown;
  error: { message: string } | null;
}): T {
  if (result.error) throw new Error(result.error.message);
  return result.data as unknown as T;
}

/* ------------------------------------------------------------- the shapes -- */

export interface Partner {
  id: string;
  owner_profile_id: string | null;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  title: string | null;
  /** "Plumber", "Electrician" (ADR 0262). Null on cards saved before it. */
  line_of_work: string | null;
  category: PartnerCategory;
  phone_mobile: string | null;
  phone_office: string | null;
  email: string | null;
  website: string | null;
  address_line1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  county: PartnerCounty;
  linkedin: string | null;
  instagram: string | null;
  facebook: string | null;
  met_at: string | null;
  met_on: string | null;
  notes: string | null;
  card_front_path: string | null;
  card_back_path: string | null;
  extraction_raw: Record<string, unknown> | null;
  extraction_confidence: Record<string, string> | null;
  sms_status: "no_consent" | "opted_in" | "opted_out";
  sms_opted_in_at: string | null;
  sms_opted_out_at: string | null;
  campaign_paused: boolean;
  last_texted_at: string | null;
  next_monthly_due_at: string | null;
  customer_id: string | null;
  created_at: string;
  owner?: { full_name: string } | null;
}

export interface PartnerMessage {
  id: string;
  partner_id: string;
  batch_id: string | null;
  direction: "outbound" | "inbound";
  kind:
    "monthly" | "storm" | "referral_intro" | "thank_you" | "manual" | "reply";
  template_id: string | null;
  storm_event_id: string | null;
  body: string;
  status: "draft" | "approved" | "sent" | "skipped" | "logged";
  skip_reason: string | null;
  sent_by: string | null;
  sent_at: string | null;
  created_at: string;
}

export interface PartnerReferral {
  id: string;
  partner_id: string;
  direction: "to_partner" | "from_partner";
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  contact_id: string | null;
  job_id: string | null;
  notes: string | null;
  outcome: "open" | "contacted" | "won" | "lost";
  created_by: string | null;
  created_at: string;
}

export interface ConsentEvent {
  id: string;
  partner_id: string;
  event: "verbal_opt_in" | "opt_out" | "re_opt_in";
  actor_profile_id: string | null;
  note: string | null;
  created_at: string;
  actor?: { full_name: string } | null;
}

export interface MessageTemplate {
  id: string;
  kind: "monthly" | "storm" | "referral_intro" | "thank_you";
  category: PartnerCategory | null;
  name: string;
  body: string;
  active: boolean;
  sort_order: number;
}

export interface CampaignBatch {
  id: string;
  kind: "monthly" | "storm";
  storm_event_id: string | null;
  status: "draft" | "in_review" | "approved" | "sending" | "done" | "cancelled";
  label: string | null;
  created_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  counts: { drafted: number; approved: number; skipped: number; sent: number };
}

export interface StormEvent {
  id: string;
  county: "shelby" | "jefferson";
  event_date: string;
  triggers: StormTrigger[];
  max_hail_in: string | number | null;
  max_wind_mph: string | number | null;
  rain_in_24h: string | number | null;
  summary: string | null;
  report_url: string | null;
  raw: Record<string, unknown>;
  batch_id: string | null;
  created_by_hand: boolean;
  created_at: string;
}

export interface StormConfigRow {
  key: string;
  value: string | number;
  unit: string;
  label: string;
  sort_order: number;
}

const PARTNER_COLUMNS = `
  id,owner_profile_id,first_name,last_name,company,title,line_of_work,category,
  phone_mobile,phone_office,email,website,
  address_line1,city,state,zip,county,
  linkedin,instagram,facebook,met_at,met_on,notes,
  card_front_path,card_back_path,extraction_raw,extraction_confidence,
  sms_status,sms_opted_in_at,sms_opted_out_at,campaign_paused,
  last_texted_at,next_monthly_due_at,customer_id,created_at,
  owner:profiles!partners_owner_profile_id_fkey(full_name)
`;

/* ----------------------------------------------------------- the partners -- */

/**
 * Everybody, in one request.
 *
 * The drawer is a few hundred cards at the outside, so the list screen filters
 * and searches in memory rather than round-tripping on every keystroke — the
 * same call `fetchContacts` makes for the same reason.
 */
export async function fetchPartners(): Promise<Partner[]> {
  return unwrap<Partner[]>(
    await supabase
      .from("partners")
      .select(PARTNER_COLUMNS)
      .order("created_at", { ascending: false }),
  );
}

export async function fetchPartner(id: string): Promise<Partner | null> {
  return row<Partner | null>(
    await supabase
      .from("partners")
      .select(PARTNER_COLUMNS)
      .eq("id", id)
      .maybeSingle(),
  );
}

export async function insertPartner(
  values: Record<string, unknown>,
): Promise<Partner> {
  return row<Partner>(
    await supabase
      .from("partners")
      .insert(values)
      .select(PARTNER_COLUMNS)
      .single(),
  );
}

export async function updatePartner(
  id: string,
  values: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase.from("partners").update(values).eq("id", id);
  if (error) throw new Error(error.message);
}

/* ------------------------------------------------------------- the cards -- */

/**
 * Store one side of a card and hand back its path.
 *
 * Keyed by partner id, so the two sides of one card sit together and a partner
 * deleted takes their photographs with them. `upsert` because a rep who
 * retakes the front should replace it rather than accumulate.
 */
export async function uploadCardImage(
  partnerId: string,
  side: "front" | "back",
  file: Blob,
): Promise<string> {
  const path = `${partnerId}/${side}.jpg`;
  const { error } = await supabase.storage
    .from("partner-cards")
    .upload(path, file, { contentType: "image/jpeg", upsert: true });
  if (error) throw new Error(error.message);
  return path;
}

/** Links good for an hour, for showing the card beside the form. */
export async function signCardImages(
  paths: readonly string[],
): Promise<Record<string, string>> {
  const wanted = paths.filter(Boolean);
  if (wanted.length === 0) return {};
  const { data, error } = await supabase.storage
    .from("partner-cards")
    .createSignedUrls(wanted as string[], 60 * 60);
  if (error) throw new Error(error.message);
  const pairs: [string, string][] = [];
  for (const row of data ?? []) {
    if (row.path && row.signedUrl) pairs.push([row.path, row.signedUrl]);
  }
  return Object.fromEntries(pairs);
}

/**
 * Ask the card reader what is on the photographs.
 *
 * Returns the raw extraction. `normalizeCardExtraction` in core turns it into
 * something storable, and the review screen is where a person looks at it.
 */
export async function readBusinessCard(input: {
  front: { media_type: string; data: string };
  back: { media_type: string; data: string } | null;
}): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.functions.invoke(
    "extract-business-card",
    { body: input },
  );

  if (error) {
    // The function reports its own trouble in the body — "the card reader is
    // busy", "not configured" — and that sentence is far more use to a rep in
    // a car park than "Edge Function returned a non-2xx status code".
    const detail = await readFunctionError(error);
    throw new Error(detail ?? error.message);
  }

  const payload = data as { extraction?: Record<string, unknown> } | null;
  if (!payload?.extraction) {
    throw new Error("The card reader gave an answer we could not read.");
  }
  return payload.extraction;
}

/** The message an Edge Function put in its response body, if it managed one. */
async function readFunctionError(error: unknown): Promise<string | null> {
  const context = (error as { context?: unknown }).context;
  if (!(context instanceof Response)) return null;
  try {
    const body = (await context.json()) as { error?: unknown };
    return typeof body.error === "string" ? body.error : null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------- consent -- */

/**
 * Record a spoken yes, or a request to stop.
 *
 * Goes through the database function rather than an update, so the status and
 * the log entry move together. See `set_partner_consent`.
 */
export async function setConsent(input: {
  partnerId: string;
  event: "verbal_opt_in" | "opt_out" | "re_opt_in";
  actorProfileId: string | null;
  note: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc("set_partner_consent", {
    p_partner: input.partnerId,
    p_event: input.event,
    p_actor: input.actorProfileId,
    p_note: input.note,
  });
  if (error) throw new Error(error.message);
}

export async function fetchConsentLog(
  partnerId: string,
): Promise<ConsentEvent[]> {
  return unwrap<ConsentEvent[]>(
    await supabase
      .from("partner_consent_events")
      .select(
        "id,partner_id,event,actor_profile_id,note,created_at," +
          "actor:profiles!partner_consent_events_actor_profile_id_fkey(full_name)",
      )
      .eq("partner_id", partnerId)
      .order("created_at", { ascending: false }),
  );
}

/* ------------------------------------------------------------ the messages -- */

const MESSAGE_COLUMNS = `
  id,partner_id,batch_id,direction,kind,template_id,storm_event_id,
  body,status,skip_reason,sent_by,sent_at,created_at
`;

export async function fetchPartnerMessages(
  partnerId: string,
): Promise<PartnerMessage[]> {
  return unwrap<PartnerMessage[]>(
    await supabase
      .from("partner_messages")
      .select(MESSAGE_COLUMNS)
      .eq("partner_id", partnerId)
      .order("created_at", { ascending: false }),
  );
}

/** Everything drafted into one batch, for the review queue. */
export async function fetchBatchMessages(
  batchId: string,
): Promise<PartnerMessage[]> {
  return unwrap<PartnerMessage[]>(
    await supabase
      .from("partner_messages")
      .select(MESSAGE_COLUMNS)
      .eq("batch_id", batchId)
      .order("created_at"),
  );
}

/**
 * One rep's approved-but-unsent messages, oldest first.
 *
 * Filtered by the *partner's* owner, which is why this is an inner join rather
 * than a column on the message: the message belongs to a partner and the
 * partner belongs to a rep, and duplicating the owner onto every message would
 * be one more thing to keep in step when a partner is reassigned.
 */
export async function fetchSendQueue(
  ownerProfileId: string,
): Promise<{ message: PartnerMessage; partner: Partner }[]> {
  const rows = unwrap<(PartnerMessage & { partner: Partner })[]>(
    await supabase
      .from("partner_messages")
      .select(`${MESSAGE_COLUMNS}, partner:partners!inner(${PARTNER_COLUMNS})`)
      .eq("status", "approved")
      .eq("direction", "outbound")
      .eq("partners.owner_profile_id", ownerProfileId)
      .order("created_at"),
  );
  return rows.map(({ partner, ...message }) => ({ message, partner }));
}

export async function insertMessage(
  values: Record<string, unknown>,
): Promise<PartnerMessage> {
  return row<PartnerMessage>(
    await supabase
      .from("partner_messages")
      .insert(values)
      .select(MESSAGE_COLUMNS)
      .single(),
  );
}

export async function updateMessage(
  id: string,
  values: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase
    .from("partner_messages")
    .update(values)
    .eq("id", id);
  if (error) throw new Error(error.message);
}

/** Mark a message sent and move the partner's clocks, in one statement. */
export async function markSent(
  messageId: string,
  senderProfileId: string | null,
): Promise<void> {
  const { error } = await supabase.rpc("mark_partner_message_sent", {
    p_message: messageId,
    p_sender: senderProfileId,
  });
  if (error) throw new Error(error.message);
}

/** The undo on the send queue. Refused by the database after a few minutes. */
export async function unsend(messageId: string): Promise<void> {
  const { error } = await supabase.rpc("unsend_partner_message", {
    p_message: messageId,
  });
  if (error) throw new Error(error.message);
}

/* ------------------------------------------------------------- the batches -- */

export async function fetchBatches(): Promise<CampaignBatch[]> {
  return unwrap<CampaignBatch[]>(
    await supabase
      .from("partner_campaign_batches")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50),
  );
}

export async function updateBatch(
  id: string,
  values: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase
    .from("partner_campaign_batches")
    .update(values)
    .eq("id", id);
  if (error) throw new Error(error.message);
}

/**
 * Draft a round of messages.
 *
 * The decision about who is in it belongs to the database. This is the button.
 */
export async function draftBatch(
  kind: "monthly" | "storm",
  stormEventId?: string,
): Promise<string> {
  const { data, error } = await supabase.rpc("draft_partner_batch", {
    p_kind: kind,
    p_storm: stormEventId ?? null,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

/* ----------------------------------------------------------- the templates -- */

export async function fetchTemplates(): Promise<MessageTemplate[]> {
  return unwrap<MessageTemplate[]>(
    await supabase
      .from("partner_message_templates")
      .select("*")
      .order("kind")
      .order("sort_order"),
  );
}

export async function saveTemplate(
  id: string | null,
  values: Record<string, unknown>,
): Promise<void> {
  const query = id
    ? supabase.from("partner_message_templates").update(values).eq("id", id)
    : supabase.from("partner_message_templates").insert(values);
  const { error } = await query;
  if (error) throw new Error(error.message);
}

/* -------------------------------------------------------------- the storms -- */

export async function fetchStormEvents(): Promise<StormEvent[]> {
  return unwrap<StormEvent[]>(
    await supabase
      .from("storm_events")
      .select("*")
      .order("event_date", { ascending: false })
      .limit(90),
  );
}

export async function fetchStormConfig(): Promise<StormConfigRow[]> {
  return unwrap<StormConfigRow[]>(
    await supabase.from("storm_config").select("*").order("sort_order"),
  );
}

export async function saveStormThreshold(
  key: string,
  value: number,
): Promise<void> {
  const { error } = await supabase
    .from("storm_config")
    .update({ value })
    .eq("key", key);
  if (error) throw new Error(error.message);
}

export async function updateStormEvent(
  id: string,
  values: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase
    .from("storm_events")
    .update(values)
    .eq("id", id);
  if (error) throw new Error(error.message);
}

/**
 * Ask the poller to look now.
 *
 * There is no scheduler in this project by decision (see the CompanyCam retry
 * note in docs/DEPLOYMENT.md), so the storms page has a button and the app
 * calls this quietly when somebody opens the partner screens. A poll is
 * idempotent — it upserts by county and date — so calling it often costs a
 * request and nothing else.
 */
export async function pollStorms(): Promise<{
  events: StormEvent[];
  errors: string[];
}> {
  const { data, error } = await supabase.functions.invoke("storm-poll", {
    body: { action: "poll" },
  });
  if (error) {
    const detail = await readFunctionError(error);
    throw new Error(detail ?? error.message);
  }
  const payload = data as { events?: StormEvent[]; errors?: string[] } | null;
  return { events: payload?.events ?? [], errors: payload?.errors ?? [] };
}

/* ----------------------------------------------------------- the referrals -- */

export async function fetchReferrals(
  partnerId?: string,
): Promise<PartnerReferral[]> {
  const query = supabase
    .from("partner_referrals")
    .select("*")
    .order("created_at", { ascending: false });
  if (partnerId) query.eq("partner_id", partnerId);
  return unwrap<PartnerReferral[]>(await query);
}

export async function insertReferral(
  values: Record<string, unknown>,
): Promise<PartnerReferral> {
  return row<PartnerReferral>(
    await supabase
      .from("partner_referrals")
      .insert(values)
      .select("*")
      .single(),
  );
}

export async function updateReferral(
  id: string,
  values: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase
    .from("partner_referrals")
    .update(values)
    .eq("id", id);
  if (error) throw new Error(error.message);
}
